import React from 'react';

/**
 * ErrorBoundary - Componente para capturar erros em toda a árvore de componentes
 * Previne que um erro em um componente quebre toda a aplicação
 *
 * Uso:
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(_error) {
    // Atualiza o estado para renderizar UI de fallback na próxima renderização
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log do erro para serviço de monitoramento (Sentry, LogRocket, etc)
    console.error('ErrorBoundary capturou um erro:', error, errorInfo);

    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // TODO: Enviar para serviço de monitoramento
    // Exemplo: Sentry.captureException(error);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    // Navegar para home
    window.location.href = '/';
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-wine-dark via-[#1e1a2e] to-wine-dark px-4">
          <div className="max-w-2xl w-full">
            {/* Card de Erro */}
            <div className="bg-gradient-to-br from-[#2a2535] to-[#1a1625] rounded-3xl p-8 md:p-12 border border-gray-700/50 shadow-2xl">
              {/* Ícone de Erro */}
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-red-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              </div>

              {/* Título */}
              <h1 className="text-white text-3xl md:text-4xl font-serif text-center mb-4">
                Ops! Algo deu errado
              </h1>

              {/* Descrição */}
              <p className="text-gray-400 text-center text-lg mb-8">
                Encontramos um problema inesperado. Não se preocupe, seus dados estão seguros.
              </p>

              {/* Detalhes do Erro (apenas em desenvolvimento) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="mb-8 bg-red-900/20 border border-red-500/30 rounded-xl p-4 overflow-auto">
                  <p className="text-red-400 font-mono text-sm mb-2">
                    <strong>Erro:</strong> {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <details className="mt-2">
                      <summary className="text-red-300 cursor-pointer hover:text-red-200 text-sm">
                        Ver stack trace
                      </summary>
                      <pre className="text-xs text-red-300/80 mt-2 overflow-auto">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              {/* Botões de Ação */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={this.handleReset}
                  className="bg-wine-gold hover:bg-wine-gold/90 text-wine-dark font-semibold px-8 py-4 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                      />
                    </svg>
                    Voltar ao Início
                  </span>
                </button>

                <button
                  onClick={this.handleReload}
                  className="bg-transparent hover:bg-white/10 text-white border-2 border-white/30 hover:border-white/60 font-semibold px-8 py-4 rounded-xl transition-all duration-300"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Recarregar Página
                  </span>
                </button>
              </div>

              {/* Informação Adicional */}
              <p className="text-gray-500 text-center text-sm mt-8">
                Se o problema persistir, entre em contato com o suporte.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
